/**
 * Property Alerts Service
 * CRUD for user-saved property search alerts.
 * When a new property is published, the notify-property-alerts edge function
 * matches it against all active alerts and sends email/SMS per user preferences.
 */

import { supabase } from '../lib/supabase';
import { gaTrackNotificationReceived } from './gaAnalyticsService';

// ─── CRUD ──────────────────────────────────────────────────────────────────────

/**
 * Fetch all alerts for the current authenticated user.
 */
export const getUserAlerts = async (userId) => {
  if (!userId) return { data: [], error: 'User not authenticated' };
  try {
    const { data, error } = await supabase
      ?.from('property_alerts')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) return { data: [], error: error?.message };
    return { data: data || [], error: null };
  } catch (err) {
    return { data: [], error: err?.message || 'Failed to fetch alerts' };
  }
};

/**
 * Create a new property alert for the current user.
 * @param {string} userId
 * @param {object} alertData - { name, commune_ids, commune_names, property_types, market_type, min_price, max_price, notify_email, notify_sms }
 */
export const createAlert = async (userId, alertData) => {
  if (!userId) return { data: null, error: 'User not authenticated' };
  try {
    const { data, error } = await supabase
      ?.from('property_alerts')
      ?.insert({
        user_id: userId,
        name: alertData?.name || 'Mon alerte',
        commune_ids: alertData?.commune_ids || [],
        commune_names: alertData?.commune_names || [],
        property_types: alertData?.property_types || [],
        market_type: alertData?.market_type || 'both',
        min_price: alertData?.min_price || null,
        max_price: alertData?.max_price || null,
        notify_email: alertData?.notify_email !== false,
        notify_sms: alertData?.notify_sms === true,
        is_active: true,
      })
      ?.select()
      ?.single();

    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err?.message || 'Failed to create alert' };
  }
};

/**
 * Update an existing alert.
 */
export const updateAlert = async (alertId, updates) => {
  if (!alertId) return { data: null, error: 'Alert ID required' };
  try {
    const { data, error } = await supabase
      ?.from('property_alerts')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', alertId)
      ?.select()
      ?.single();

    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err?.message || 'Failed to update alert' };
  }
};

/**
 * Toggle alert active/inactive.
 */
export const toggleAlert = async (alertId, isActive) => {
  return updateAlert(alertId, { is_active: isActive });
};

/**
 * Delete an alert.
 */
export const deleteAlert = async (alertId) => {
  if (!alertId) return { error: 'Alert ID required' };
  try {
    const { error } = await supabase
      ?.from('property_alerts')
      ?.delete()
      ?.eq('id', alertId);

    if (error) return { error: error?.message };
    return { error: null };
  } catch (err) {
    return { error: err?.message || 'Failed to delete alert' };
  }
};

// ─── TRIGGER NOTIFICATIONS ─────────────────────────────────────────────────────

/**
 * Trigger the notify-property-alerts edge function after a new property is created.
 * Called silently — never blocks the main flow.
 * @param {string} propertyId
 */
export const triggerPropertyAlertNotifications = async (propertyId) => {
  if (!propertyId) return;
  try {
    const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
    const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;
    if (!SUPABASE_URL) return;

    const { data: sessionData } = await supabase?.auth?.getSession();
    const accessToken = sessionData?.session?.access_token || SUPABASE_ANON_KEY;

    const response = await fetch(`${SUPABASE_URL}/functions/v1/notify-property-alerts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ property_id: propertyId }),
    });

    if (response?.ok) {
      // Track in GA — Funnel Stage 3 (property alert notification dispatched)
      gaTrackNotificationReceived({ channel: 'email', type: 'property_alert', propertyId });
    }
  } catch (err) {
    // Silent fail — notifications never block the main flow
    console.warn('[PropertyAlerts] Trigger notification failed silently:', err?.message);
  }
};
